"use client";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { contactFormSchema, type ContactFormInput } from "@/lib/validations/contact";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Mail, MapPin, Phone, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState<string>("");

  const form = useForm<ContactFormInput>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  // Auto-clear messages after 10 seconds
  useEffect(() => {
    if (submitStatus === "success" || submitStatus === "error") {
      const timer = setTimeout(() => {
        setSubmitStatus("idle");
        setSubmitMessage("");
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  async function onSubmit(data: ContactFormInput) {
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus("success");
        setSubmitMessage(
          result.message || "Thank you for your message! We will get back to you soon."
        );
        form.reset();
      } else {
        setSubmitStatus("error");
        // Handle rate limiting error (429)
        if (response.status === 429) {
          const retryAfter = result.retryAfter || 900; // Default to 15 minutes
          const minutes = Math.ceil(retryAfter / 60);
          setSubmitMessage(
            `Too many requests. Please wait ${minutes} minute${minutes > 1 ? "s" : ""} before trying again.`
          );
        } else {
          setSubmitMessage(result.error || "Failed to send message. Please try again.");
        }
      }
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage("An unexpected error occurred. Please try again later.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section
      id="contact"
      className="relative isolate w-full px-4 py-12 sm:px-6 sm:py-16 md:py-20 lg:px-8"
    >
      {/* Fancy gradient background - continues from CTA bottom left and bottom right */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Base gradient - starts with CTA's ending colors at top left and top right */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 100% 80% at top left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at top center, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at top right, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at left center, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at right center, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom right, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.15) 0%, transparent 40%, oklch(0.769 0.188 70.08 / 0.1) 100%),
              oklch(1 0 0)
            `,
          }}
        />

        {/* Animated floating orbs */}
        <div className="bg-chart-1/20 absolute top-20 -left-32 size-96 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse" />
        <div className="bg-chart-4/20 absolute -right-32 bottom-20 size-80 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-1000" />
        <div className="bg-chart-2/15 absolute top-1/2 left-1/2 size-72 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-2000" />

        {/* Subtle mesh gradient overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(to bottom right, transparent 0%, oklch(0.646 0.222 41.116 / 0.05) 30%, oklch(0.828 0.189 84.429 / 0.05) 70%, transparent 100%)",
          }}
        />
      </div>

      <div className="container mx-auto">
        <div className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 mb-8 motion-safe:duration-700">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{
              backgroundImage:
                "linear-gradient(to right, oklch(0.145 0 0), oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Contact
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">Let&apos;s plan your session.</p>
        </div>
        <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:gap-12">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="group bg-card motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-left-2 space-y-4 rounded-xl border p-4 shadow-xs transition-all duration-300 hover:shadow-md motion-safe:duration-700 sm:p-5 lg:p-6"
              style={{
                background: `
                  linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.05), oklch(0.828 0.189 84.429 / 0.03)),
                  oklch(1 0 0)
                `,
              }}
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="group-focus-within:text-primary">Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your name"
                        disabled={isSubmitting}
                        className="bg-background/50"
                        style={{
                          background: `
                            linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.03), oklch(0.828 0.189 84.429 / 0.02)),
                            oklch(1 0 0)
                          `,
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="group-focus-within:text-primary">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="your.email@example.com"
                        disabled={isSubmitting}
                        className="bg-background/50"
                        style={{
                          background: `
                            linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.03), oklch(0.828 0.189 84.429 / 0.02)),
                            oklch(1 0 0)
                          `,
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="group-focus-within:text-primary">Message</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us about your project..."
                        rows={5}
                        disabled={isSubmitting}
                        className="bg-background/50 max-h-40 resize-none"
                        style={{
                          background: `
                            linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.03), oklch(0.828 0.189 84.429 / 0.02)),
                            oklch(1 0 0)
                          `,
                        }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {submitStatus === "success" && submitMessage && (
                <div
                  className="animate-in fade-in-0 text-xs sm:text-sm"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {submitMessage}
                </div>
              )}
              {submitStatus === "error" && submitMessage && (
                <div className="text-destructive animate-in fade-in-0 text-xs sm:text-sm">
                  {submitMessage}
                </div>
              )}
              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="relative w-full overflow-hidden transition-all duration-300 hover:scale-105 sm:w-auto"
                  style={{
                    background:
                      "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                    color: "white",
                    border: "none",
                  }}
                >
                  {submitStatus === "success" ? (
                    <>
                      <Check className="size-4" />
                      Sent
                    </>
                  ) : (
                    <>
                      <Send className="size-4" />
                      {isSubmitting ? "Sending..." : "Send message"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>

          <div
            className="group bg-card motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-right-2 rounded-xl border p-4 shadow-xs transition-all duration-300 hover:shadow-md motion-safe:delay-150 motion-safe:duration-700 sm:p-5 lg:p-6"
            style={{
              background: `
                linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.05), oklch(0.828 0.189 84.429 / 0.03)),
                oklch(1 0 0)
              `,
            }}
          >
            <h3
              className="text-base font-medium transition-colors duration-300 sm:text-lg"
              style={{
                backgroundImage:
                  "linear-gradient(to right, oklch(0.145 0 0), oklch(0.646 0.222 41.116))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Booking info
            </h3>
            <p className="text-muted-foreground mt-1 text-xs sm:text-sm">Mon–Sat, 9:00–18:00 IST</p>
            <div className="mt-3 space-y-2.5 text-xs sm:mt-4 sm:space-y-3 sm:text-sm">
              <div className="flex items-center gap-2 transition-all duration-300 hover:translate-x-1">
                <Mail
                  className="size-4 opacity-70 transition-transform duration-300 group-hover:scale-110"
                  aria-hidden
                />
                <a
                  href="mailto:gdgroupstudio@gmail.com"
                  className="focus-visible:ring-ring transition-colors duration-300 hover:underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                  gdgroupstudio@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-2 transition-all duration-300 hover:translate-x-1">
                <Phone
                  className="size-4 opacity-70 transition-transform duration-300 group-hover:scale-110"
                  aria-hidden
                />
                <div className="flex flex-col">
                  <a
                    href="tel:+919936701147"
                    className="focus-visible:ring-ring transition-colors duration-300 hover:underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    +91 99367 01147
                  </a>
                  <a
                    href="tel:+917572022775"
                    className="focus-visible:ring-ring transition-colors duration-300 hover:underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    +91 75720 22775
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2 transition-all duration-300 hover:translate-x-1">
                <MapPin
                  className="mt-0.5 size-4 opacity-70 transition-transform duration-300 group-hover:scale-110"
                  aria-hidden
                />
                <address className="leading-relaxed not-italic">
                  Gadaiya Lonipar, Naribari
                  <br />
                  Prayagraj, Uttar Pradesh 212106
                  <br />
                  India
                </address>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
